import cx from 'clsx';
import Tree from 'rc-tree';
import type { CheckInfo } from 'rc-tree/lib/Tree';
import type { Key } from 'rc-tree/lib/interface';
import React, { useEffect, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';

import { ReactComponent as ClearIcon } from '@assets/svg/cross.svg';
import { ReactComponent as SearchIcon } from '@assets/svg/search.svg';
import { ReactComponent as ThinArrowIcon } from '@assets/svg/thin-arrow.svg';

import Input from '@components/uikit/Input/Input';

import useTranslation from '@hooks/useTranslation';

import type { ILocation } from '@model/structureData';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import CheckedItemsBlock from './components/CheckedItemsBlock/CheckedItemsBlock';
import { getAllowedLocations } from './helpers/getAllowedLocations';
import { getTreeCheckedNodes } from './helpers/getTreeCheckedNodes';
import style from './style.module.scss';

interface IProps {
  control: any;
  locationsFormName: string;
  facilityStructureData: ILocation[];
  variant: string;
  showSearch?: boolean;
  showSelectAll?: boolean;
  disabled?: boolean;
  expanded?: boolean;
  showAllFacilitiesBlock?: boolean;
}

const FacilityTreeBlock: React.FC<IProps> = ({
  control,
  locationsFormName,
  facilityStructureData,
  variant,
  showSearch,
  showSelectAll,
  disabled,
  expanded = true,
  showAllFacilitiesBlock,
}) => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);

  const { data: assetTreeData } = useAssetTreeQuery();

  const allowedLocations = useMemo(() => getAllowedLocations(facilityStructureData), [facilityStructureData]);
  const allowedTreeNodes = useMemo(() => getTreeCheckedNodes(allowedLocations), [allowedLocations]);

  const locationsActive = assetTreeData?.locations || [];

  const {
    field: { value: locationsValue, onChange: locationsChange },
  } = useController({
    control,
    name: locationsFormName,
  });

  const typedLocationsValue = locationsValue as number[];
  const selectedLocationsValue = new Set(typedLocationsValue);

  const isSelectedAll =
    allowedTreeNodes.length === selectedLocationsValue.size &&
    allowedTreeNodes.every((id) => selectedLocationsValue.has(id));

  useEffect(() => {
    if (showAllFacilitiesBlock) {
      setExpandedKeys([]);
      setSearchValue('');
    }
  }, [showAllFacilitiesBlock]);

  const onCheck = (info: CheckInfo<ILocation>) => {
    const { checkedNodes } = info;

    const treeCheckedNodes = getTreeCheckedNodes(checkedNodes);
    locationsChange(treeCheckedNodes);
  };

  const onSearchChange = (value: string) => {
    setExpandedKeys(value ? locationsActive.map((location) => location.id) : []);
    setSearchValue(value);
  };

  const handleDelete = (id: string) => {
    const filteredLocs = typedLocationsValue.filter((locId) => +locId !== +id);

    locationsChange(filteredLocs);
  };

  const clearAllChecked = () => {
    locationsChange([]);
  };

  const onSelectAll = (checked: boolean) => {
    const checkAllValue = checked ? getTreeCheckedNodes(allowedLocations) : [];
    locationsChange(checkAllValue);
    if (checked) {
      const expandedKeys: Key[] = [];
      locationsActive.forEach((location) => {
        if (location.children) {
          expandedKeys.push(location.id);
        }
      });
      setExpandedKeys(expandedKeys);
    }
  };

  const filterTreeNode = (node: ILocation) => {
    if (searchValue) {
      const result = node.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;

      return !!result;
    }
    return false;
  };

  return (
    <div className={style.facilitiesSelect}>
      <div className={style.facilitiesBlock}>
        {showSearch && (
          <div className={style.searchWrapper}>
            <Input
              leftElement={<SearchIcon className={style.searchIcon} />}
              placeholder={t('Search...')}
              value={searchValue}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => onSearchChange(target.value)}
              dataQa={'search'}
              rightElement={
                searchValue && <ClearIcon className={style.closeIcon} onClick={() => setSearchValue('')} />
              }
            />
          </div>
        )}
        <div className={style.checkableTreeBlock}>
          <div
            className={cx(style.scrollBlock, {
              [style[variant]]: !!variant,
            })}
          >
            <Tree
              className={style.treeComponent}
              treeData={facilityStructureData}
              checkedKeys={typedLocationsValue}
              expandedKeys={expanded ? expandedKeys : []}
              selectable={false}
              checkable={true}
              showLine={false}
              showIcon={false}
              multiple={true}
              disabled={disabled}
              fieldNames={{
                children: 'children',
                title: 'noTreeTitle',
                key: 'id',
              }}
              switcherIcon={expanded && <ThinArrowIcon />}
              titleRender={(node) => node.name}
              filterTreeNode={filterTreeNode}
              onCheck={(_, info) => onCheck(info)}
              onExpand={(expandedKeysValue) => setExpandedKeys(expandedKeysValue)}
            />
          </div>
          {showSelectAll && (
            <label className={style.checkboxWrapper}>
              <input
                type="checkbox"
                className={style.checkboxInput}
                checked={isSelectedAll}
                onChange={({ target }) => onSelectAll(target.checked)}
              />
              <div className={style.checkbox}>{isSelectedAll && <div className={style.isChecked} />}</div>
              <p className={style.checkboxLabel}>{t('Select all')}</p>
            </label>
          )}
        </div>
      </div>
      <CheckedItemsBlock
        showAllFacilitiesBlock={showAllFacilitiesBlock}
        locationsIds={typedLocationsValue}
        clearAllChecked={clearAllChecked}
        handleDelete={handleDelete}
        treeBlockVariant={variant}
      />
    </div>
  );
};

export default FacilityTreeBlock;
